import { OwcIconButton, OwcListItem, OwcMenu, OwcTooltip } from "@one/react";
import { react18WorkaroundProps } from "../../utils/constants/constants";
import moment from "moment-timezone";
import { MouseEvent, ReactNode, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { RootStore } from "../../reducers";
import { BookingRewireDataType } from "../../Models/BookingRewireDataType";
import { getLogParamsList } from "../bookings/BookingMenuAction";
import { SubMenu } from "@digitallab/grid-common-frontend-components";

const CoverMenuItem = styled.div`
  height: 0.973480224609375px;
  width: 100%;
  border-radius: 0;
  background: var(--one-color-gray-300);
`;

type BookingMenuActionParams = {
  params: {
    data: BookingRewireDataType;
  };
  handlers: {
    deleteEquipment: (params?: { type?: "end" | "cancel" }) => void;
    editBooking: (data: BookingRewireDataType) => void;
    setRowEquipmentId: React.Dispatch<React.SetStateAction<string>>;
    setPopupEnableStatus: React.Dispatch<React.SetStateAction<boolean>>;
    rejectEquipment?: () => void;
    setRejectRowEquipmentId?: React.Dispatch<React.SetStateAction<string>>;
    changeEndTimeOpen?: (params: any) => void;
  };
};

type EndMenuItemProps = {
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLOwcIconButtonElement | null>>;
  deleteEquipment: (params?: { type?: "end" | "cancel" }) => void;
  params: {
    data: BookingRewireDataType;
  };
  currentTimeString: string;
  changeEndTimeOpen?: (params: { data: BookingRewireDataType }) => void;
};

export const EndMenuItem = ({
  setAnchorEl,
  deleteEquipment,
  params,
  currentTimeString,
  changeEndTimeOpen,
}: EndMenuItemProps) =>
  (() => {
    const isDisabled =
      !params?.data?.active ||
      !(currentTimeString > params?.data?.dateFrom && currentTimeString < params?.data?.dateTo);

    return (
      <SubMenu
        label="End"
        testId="simple-menu-end-actions"
        submenuTestId="simple-menu-end-submenu"
        itemCount={2}
        disabled={isDisabled}
      >
        {() => (
          <>
            <OwcListItem
              data-testid="simple-menu-view-end-now"
              onClick={() => {
                setAnchorEl(null);
                deleteEquipment({ type: "end" });
              }}
              disabled={isDisabled}
              {...react18WorkaroundProps}
            >
              End now
            </OwcListItem>
            <OwcListItem
              data-testid="simple-menu-view-change-endtime"
              onClick={() => {
                setAnchorEl(null);
                if (changeEndTimeOpen) {
                  changeEndTimeOpen(params);
                  return;
                }
                deleteEquipment({ type: "end" });
              }}
              disabled={isDisabled}
              {...react18WorkaroundProps}
            >
              Change endtime
            </OwcListItem>
          </>
        )}
      </SubMenu>
    );
  })();

const BookingMenuAction = ({
  params, // eslint-disable-next-line no-unused-vars
  handlers: {
    deleteEquipment,
    editBooking,
    setRowEquipmentId,
    setPopupEnableStatus,
    rejectEquipment,
    setRejectRowEquipmentId,
    changeEndTimeOpen,
  },
}: BookingMenuActionParams) => {
  const user = useSelector((store: RootStore) => store.user);
  const [anchorEl, setAnchorEl] = useState<HTMLOwcIconButtonElement | null>(null);
  const uniqueAnchorId = `my-booking-menu-${params?.data?.id}`;
  const currentTimeString = moment.tz(user.timezone).utc().format();
  const now = moment.tz(user.timezone);
  const bookingStart = moment(params?.data?.dateFrom);
  const bookingEnd = moment(params?.data?.dateTo);

  const handleSettingClose = () => {
    setAnchorEl(null);
  };
  const handleSettingClick = (
    event: MouseEvent<HTMLOwcIconButtonElement, globalThis.MouseEvent>,
    params: { data: BookingRewireDataType },
  ) => {
    setRowEquipmentId?.(params?.data?.id);
    setAnchorEl(event.currentTarget);
  };

  const userDoesNotHaveRightsToWriteInLogbook =
    !user.roles.includes("LogbookAdmin") && !user.roles.includes("LogbookWriter");
  return (
    <>
      <OwcIconButton
        icon="more_vertical"
        type="legacy"
        flat
        onClick={(event) => {
          handleSettingClick(event, params);
        }}
        id={uniqueAnchorId}
        data-testid="booking-menu-toggle"
        disabled={!params?.data?.active}
        {...react18WorkaroundProps}
      />
      {ReactDOM.createPortal(
        (
          <OwcMenu
            data-testid="simple-menu"
            scrollPadding={false}
            anchor={uniqueAnchorId}
            visible={!!anchorEl}
            onClickOutside={handleSettingClose}
            disablePortal={true}
            {...react18WorkaroundProps}
          >
            <OwcListItem
              data-testid="simple-menu-filter"
              onClick={() => {
                setAnchorEl(null);
                setPopupEnableStatus(true);
                editBooking(params?.data);
              }}
              disabled={
                !params?.data?.active ||
                params?.data?.checkInDone ||
                (params?.data?.checkInRequired && bookingStart.isSameOrBefore(now)) ||
                bookingEnd.isBefore(now)
              }
              {...react18WorkaroundProps}
            >
              Edit
            </OwcListItem>
            <div id={`AddLogMenuOption-${params?.data?.id}`}>
              <OwcListItem
                data-testid="simple-menu-filter"
                onClick={() => {
                  setAnchorEl(null);
                  const logParamsList = getLogParamsList(params.data);
                  logParamsList.forEach((url) => {
                    window.open(`${process.env.REACT_APP_LOGBOOK_APP_URL}${url}`, "_blank");
                  });
                }}
                disabled={userDoesNotHaveRightsToWriteInLogbook}
                {...react18WorkaroundProps}
              >
                Add log
              </OwcListItem>

              {userDoesNotHaveRightsToWriteInLogbook && (
                <OwcTooltip anchor={`AddLogMenuOption-${params?.data?.id}`} {...react18WorkaroundProps}>
                  You do not have permissions to create log entry.
                </OwcTooltip>
              )}
            </div>
            <OwcListItem
              data-testid="simple-menu-duplicate"
              onClick={() => {
                setAnchorEl(null);
                setPopupEnableStatus(true);
                const duplicated = { ...params.data, isDuplicate: true };

                editBooking(duplicated);
              }}
              disabled={!params?.data?.active}
              {...react18WorkaroundProps}
            >
              Duplicate
            </OwcListItem>
            <EndMenuItem
              setAnchorEl={setAnchorEl}
              deleteEquipment={deleteEquipment}
              params={params}
              currentTimeString={currentTimeString}
              changeEndTimeOpen={changeEndTimeOpen}
            />
            <CoverMenuItem />
            <OwcListItem
              data-testid="simple-menu-filter"
              onClick={() => {
                setAnchorEl(null);
                deleteEquipment();
              }}
              disabled={!params?.data?.active || bookingStart.isBefore(now) || bookingEnd.isBefore(now)}
              {...react18WorkaroundProps}
            >
              Cancel
            </OwcListItem>
          </OwcMenu>
        ) as ReactNode,
        document.body,
      )}
    </>
  );
};
export default BookingMenuAction;
